import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowUp } from 'lucide-react'
import { PageHeader } from '@/components/PageHeader'
import { Section } from '@/components/Section'
import { cn } from '@/lib/utils'

export interface LegalSectionItem {
  id: string
  number: string
  title: string
}

interface LegalPageLayoutProps {
  /** i18n namespace root, e.g. 'legal.privacy' */
  documentNumber: string
  eyebrow: string
  title: string
  subtitle: string
  intro: string
  /** Table of contents items (id + number + title) */
  toc: LegalSectionItem[]
  children: ReactNode
}

/**
 * LegalPageLayout — shared scaffolding for Privacy, Cookie Policy and
 * Legal Notice. Implements a two-column editorial layout that reads
 * like an engineering technical document: a sticky left-side index
 * (TOC) with numeric prefixes on desktop, and a primary content column
 * on the right. A metadata strip above the content mirrors an
 * engineering drawing title block (DOC number, version, last-updated).
 *
 * Visual language: ink-100 background, white content card with a
 * sharp brand-500 top stripe, left engineering rail, sharp corners,
 * monospace/tabular numerals for DOC numbers.
 */
export function LegalPageLayout({
  documentNumber,
  eyebrow,
  title,
  subtitle,
  intro,
  toc,
  children,
}: LegalPageLayoutProps) {
  const { t } = useTranslation()
  const [activeId, setActiveId] = useState<string | null>(toc[0]?.id ?? null)
  const [showBackToTop, setShowBackToTop] = useState(false)

  // Spy on section visibility to highlight the active TOC item.
  useEffect(() => {
    if (toc.length === 0) return
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('id')
            if (id) setActiveId(id)
          }
        })
      },
      {
        rootMargin: '-20% 0px -70% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    )
    toc.forEach(({ id }) => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [toc])

  // Back-to-top button only after scrolling past hero.
  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 700)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <main id="main-content" className="bg-ink-100">
      <PageHeader
        variant="light"
        eyebrow={eyebrow}
        title={title}
        subtitle={subtitle}
      />

      {/* Document metadata strip — engineering title block */}
      <div className="border-b border-ink-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <dl className="grid grid-cols-2 md:grid-cols-4 divide-x divide-ink-200">
            <MetaField
              label={t('legal.common.documentLabel')}
              value={`${t('legal.common.documentNumber')} ${documentNumber}`}
            />
            <MetaField
              label={t('legal.common.versionLabel')}
              value={t('legal.common.versionValue')}
            />
            <MetaField
              label={t('legal.common.lastUpdated')}
              value={t('legal.common.lastUpdatedValue')}
              className="col-span-2 md:col-span-2"
            />
          </dl>
        </div>
      </div>

      <Section tone="default" spacing="lg" width="wide">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Left: sticky TOC rail */}
          <aside className="lg:col-span-4 xl:col-span-3">
            <nav
              aria-label={t('legal.common.tocTitle')}
              className="lg:sticky lg:top-28"
            >
              <div className="relative bg-white border border-ink-200 shadow-sm">
                <span
                  aria-hidden="true"
                  className="absolute top-0 left-0 right-0 h-[3px] bg-brand-500"
                />
                <span
                  aria-hidden="true"
                  className="absolute top-3 right-4 font-display font-bold text-[10px] tracking-[0.22em] uppercase text-ink-400"
                >
                  N° {documentNumber}
                </span>

                <header className="px-5 pt-8 pb-4 border-b border-ink-200">
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-brand-600 inline-flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="block h-px w-6 bg-brand-500"
                    />
                    {t('legal.common.tocTitle')}
                  </p>
                </header>

                <ol className="relative py-2">
                  {toc.map((item) => {
                    const isActive = activeId === item.id
                    return (
                      <li key={item.id}>
                        <a
                          href={`#${item.id}`}
                          className={cn(
                            'group relative flex items-start gap-3 py-2.5 pr-4 pl-5 text-[13px] leading-snug transition-colors',
                            isActive
                              ? 'text-ink-900 font-semibold bg-ink-50'
                              : 'text-ink-600 hover:text-ink-900 hover:bg-ink-50'
                          )}
                        >
                          {/* Left rail — turns blue when active */}
                          <span
                            aria-hidden="true"
                            className={cn(
                              'absolute left-0 top-0 bottom-0 w-[3px] transition-colors',
                              isActive
                                ? 'bg-brand-500'
                                : 'bg-transparent group-hover:bg-brand-500/40'
                            )}
                          />
                          <span
                            className={cn(
                              'font-display font-bold tabular-nums text-[11px] tracking-[0.1em] pt-[2px] shrink-0',
                              isActive ? 'text-brand-600' : 'text-ink-400'
                            )}
                          >
                            {item.number}
                          </span>
                          <span className="fx-text-pretty">{item.title}</span>
                        </a>
                      </li>
                    )
                  })}
                </ol>
              </div>
            </nav>
          </aside>

          {/* Right: content column */}
          <div className="lg:col-span-8 xl:col-span-9">
            <article className="relative bg-white border border-ink-200 shadow-sm">
              <span
                aria-hidden="true"
                className="absolute top-0 left-0 right-0 h-[3px] bg-brand-500"
              />
              {/* Corner engineering ticks */}
              <span
                aria-hidden="true"
                className="absolute top-4 left-4 block h-3 w-px bg-brand-500/60"
              />
              <span
                aria-hidden="true"
                className="absolute top-4 left-4 block h-px w-3 bg-brand-500/60"
              />
              <span
                aria-hidden="true"
                className="absolute bottom-4 right-4 block h-3 w-px bg-brand-500/60"
              />
              <span
                aria-hidden="true"
                className="absolute bottom-4 right-4 block h-px w-3 bg-brand-500/60"
              />

              <div className="px-6 py-10 sm:px-10 sm:py-12 lg:px-14 lg:py-14">
                {/* Intro paragraph — editorial lead */}
                <p className="fx-text-pretty text-base md:text-[17px] leading-[1.75] text-ink-700 max-w-3xl">
                  <span
                    aria-hidden="true"
                    className="float-left mr-3 mt-[10px] block h-[3px] w-10 bg-brand-500"
                  />
                  {intro}
                </p>

                {children}
              </div>
            </article>
          </div>
        </div>
      </Section>

      {/* Back to top button */}
      <button
        type="button"
        onClick={() =>
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
        aria-label={t('legal.common.backToTop')}
        className={cn(
          'fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 bg-ink-900 text-white px-4 py-2.5 rounded-sm text-xs font-bold uppercase tracking-[0.18em]',
          'border border-ink-700 hover:bg-brand-500 hover:border-brand-500 transition-colors',
          'shadow-[0_10px_25px_-10px_rgba(0,0,0,0.4)]',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2',
          showBackToTop
            ? 'opacity-100 pointer-events-auto translate-y-0'
            : 'opacity-0 pointer-events-none translate-y-2',
          'duration-200'
        )}
      >
        <ArrowUp className="h-3.5 w-3.5" strokeWidth={2.75} aria-hidden="true" />
        <span className="hidden sm:inline">{t('legal.common.backToTop')}</span>
      </button>
    </main>
  )
}

function MetaField({
  label,
  value,
  className,
}: {
  label: string
  value: string
  className?: string
}) {
  return (
    <div className={cn('px-4 sm:px-6 py-4 md:py-5 first:pl-0', className)}>
      <dt className="text-[10px] font-bold uppercase tracking-[0.22em] text-ink-500">
        {label}
      </dt>
      <dd className="mt-1 font-display font-bold tabular-nums text-sm md:text-base text-ink-900">
        {value}
      </dd>
    </div>
  )
}

/* ============================================================
   LegalSection — canonical block for a numbered legal section.
   Adds an anchor id, a numeric prefix header, a body paragraph
   and optional list items in the industrial style.
   ============================================================ */
interface LegalSectionProps {
  id: string
  number: string
  title: string
  body?: string
  children?: ReactNode
}

export function LegalSection({
  id,
  number,
  title,
  body,
  children,
}: LegalSectionProps) {
  return (
    <section
      id={id}
      aria-labelledby={`${id}-heading`}
      className="scroll-mt-28 mt-14 first-of-type:mt-14"
    >
      <header className="mb-6 flex items-baseline gap-4 pb-4 border-b border-ink-200">
        <span
          aria-hidden="true"
          className="font-display font-bold tabular-nums text-xs tracking-[0.18em] text-brand-600"
        >
          {number}
        </span>
        <h2
          id={`${id}-heading`}
          className="fx-text-balance font-display font-bold leading-tight text-ink-900 text-xl sm:text-2xl md:text-[1.625rem]"
        >
          {title}
        </h2>
      </header>

      {body && (
        <p className="fx-text-pretty text-[15px] md:text-base leading-[1.8] text-ink-700 max-w-3xl">
          {body}
        </p>
      )}

      {children && <div className="mt-5">{children}</div>}
    </section>
  )
}

/* ============================================================
   LegalList — ordered list with engineered marker.
   Used for "items" arrays in the privacy/cookie policy content.
   ============================================================ */
export function LegalList({ items }: { items: string[] }) {
  return (
    <ul className="mt-4 max-w-3xl space-y-3.5">
      {items.map((item, i) => (
        <li
          key={i}
          className="relative flex items-start gap-4 pl-5 text-[15px] leading-[1.75] text-ink-700"
        >
          <span
            aria-hidden="true"
            className="absolute left-0 top-3 block h-[2px] w-3 bg-brand-500"
          />
          <span className="font-display font-bold tabular-nums text-[11px] tracking-[0.12em] text-ink-400 pt-1 shrink-0">
            {String(i + 1).padStart(2, '0')}
          </span>
          <span className="fx-text-pretty">{item}</span>
        </li>
      ))}
    </ul>
  )
}

/* ============================================================
   LegalCallout — informative/attention box used for notes
   like placeholders or cookie-category disclaimers.
   ============================================================ */
export function LegalCallout({
  label,
  children,
  tone = 'info',
}: {
  label?: string
  children: ReactNode
  tone?: 'info' | 'warning'
}) {
  return (
    <aside
      className={cn(
        'relative mt-6 max-w-3xl bg-ink-50 border border-ink-200 px-5 py-4 text-[14px] leading-relaxed text-ink-700',
        tone === 'warning' && 'border-l-[3px] border-l-brand-500'
      )}
    >
      {label && (
        <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-brand-600">
          {label}
        </p>
      )}
      <div className="fx-text-pretty">{children}</div>
    </aside>
  )
}
